import {TiVendorMicrosoft, TiWeatherPartlySunny, TiWeatherSunny } from "react-icons/ti";
import {BsBucket, BsCalendarDate } from 'react-icons/bs';
import {MdImageSearch, MdOutlineEventNote, MdTranslate } from 'react-icons/md';
import { AiOutlineApi } from 'react-icons/ai';
import {TbBrandGoogle, TbDatabaseLeak, TbDatabaseSearch, TbUserQuestion, TbWorldSearch } from 'react-icons/tb';
import {GoArchive, GoDiscussionOutdated } from 'react-icons/go';
import {FaCode} from "react-icons/fa6";
import { LuBookMarked, LuImagePlus } from 'react-icons/lu';
import {PiMathOperationsBold, PiSpeakerHighBold } from 'react-icons/pi';
import {IoLibraryOutline, IoSpeedometerOutline } from 'react-icons/io5';
import {TfiPin} from "react-icons/tfi";
import { GiPathDistance } from 'react-icons/gi';
import {RiGovernmentLine, RiPresentationLine } from 'react-icons/ri';
import { HiOutlineBuildingLibrary } from "react-icons/hi2";
import {SiArxiv, SiThemoviedatabase} from "react-icons/si";

export enum AgentSkillType {
    LLM_BASE_KNOWLEDGE = 'LLM_BASE_KNOWLEDGE',
    CONVERSATION_HISTORY = 'CONVERSATION_HISTORY',
    DATE_AND_TIME = 'DATE_AND_TIME',  // with or without location
    FILE_BUCKET = 'FILE_BUCKET',
    RT_RAG = 'RT_RAG',
    WEB_SEARCH = 'WEB_SEARCH',  // Full or on certain pages, with or without restriction on certraon css selectors
    WEATHER_CURRENT = 'WEATHER_CURRENT',
    WEATHER_FORECAST = 'WEATHER_FORECAST',
    ROUTING = 'ROUTING',
    POINT_OF_INTERESTS = 'POINT_OF_INTERESTS',
    CALCULATOR = 'CALCULATOR',
    TRANSLATOR = 'TRANSLATOR',
    IMAGE_UNDERSTANDING = 'IMAGE_UNDERSTANDING',
    IMAGE_CREATION = 'IMAGE_CREATION',
    TEXT_TO_SPEECH = 'TEXT_TO_SPEECH',
    INTERVIEWS = 'INTERVIEWS',
    DE_VH_KURSE = 'VH_KURSE_DE',
    DE_SERVICE_BW = 'SERVICE_BW',
    DE_DIGITALE_BIB = 'DE_DIGITALE_BIB',
    OPEN_LIBRARY = 'OPEN_LIBRARY',
    GOOGLE_BOOKS = 'GOOGLE_BOOKS',
    ARXIV = 'ARXIV',
    DE_TEXTARCHIV = 'DE_TEXTARCHIV',
    TMDB = 'TMDB',
    ENAIO = 'ENAIO',
    PIROBASE = 'PIROBASE',
    SHAREPOINT = 'SHAREPOINT',
    STAFFBASE = 'STAFFBASE',    
    OPARL = 'OPARL',
    CUSTOM_API = 'CUSTOM_API',
    KOMMONE_EVENT = 'KOMMONE_EVENT',
}

export interface AgentSkillTypeInfo {
    type: AgentSkillType;
    name: string;
    description: string;
    icon: React.ComponentType;
    configurationFields?: Array<AgentSkillSetting>;
}

// the actual instances of an AgentSkillTye, configured by the user
export interface AgentSkill {
    id: string,
    type: AgentSkillType;
    name: string;
    description: string;  // when to use this skill
    status: 'active' | 'inactive' | 'error';
    configuration: Record<string, any>;
    lastUpdated?: Date;
}

export interface AgentSkillSetting {
    key: string;
    label: string;
    type: 'text' | 'url' | 'select' | 'number' | 'textarea';
    required: boolean;
    options?: string[];
}

export let AGENT_SKILL_TYPES: Record<AgentSkillType, AgentSkillTypeInfo>;
AGENT_SKILL_TYPES = {

    [AgentSkillType.LLM_BASE_KNOWLEDGE]: {
        type: AgentSkillType.LLM_BASE_KNOWLEDGE,
        name: 'LLM Basiswissen',
        description: 'Der Agent kann das Basiswissen des LLMs nutzen.',
        icon: LuBookMarked,
    },

    [AgentSkillType.CONVERSATION_HISTORY]: {
        type: AgentSkillType.CONVERSATION_HISTORY,
        name: 'Chatverlauf',
        description: 'Der Agent kennt den Chatverlauf der Session.',
        icon: GoDiscussionOutdated,
    },

    [AgentSkillType.DATE_AND_TIME]: {
        type: AgentSkillType.DATE_AND_TIME,
        name: 'Datum und Uhrzeit',
        description: 'Der Agent kennt das aktuelle Datum und die aktuelle Uhrzeit.',
        icon: BsCalendarDate,
        configurationFields: [
            {
                key: 'timezone',
                label: 'Zeitzone',
                type: 'text',
                required: false
            },
        ]
    },

    [AgentSkillType.FILE_BUCKET]: {
        type: AgentSkillType.FILE_BUCKET,
        name: 'Dokumenten-Eimer',
        description: 'Der Agent kennt den Inhalt aller Dateien in diesem Eimer.',
        icon: BsBucket,
        configurationFields: [
            {
                key: 'bucket_name',
                label: 'Name des Dokumenten-Eimers',
                type: 'text',
                required: true
            },
            {
                key: 'bucket_description',
                label: 'Beschreibung des Inhalts',
                type: 'text',
                required: true
            },
        ]
    },

    [AgentSkillType.RT_RAG]: {
        type: AgentSkillType.RT_RAG,
        name: 'Echtzeit-RAG',
        description: 'Der Agent kann das realtime-RAG pattern nutzen.',
        icon: IoSpeedometerOutline,
        configurationFields: [
            {
                key: 'web_rag_city',
                label: 'Always relate your Answer to...',
                type: 'text',
                required: true
            },
            {
                key: 'web_rag_base_url',
                label: 'Base URL:',
                type: 'text',
                required: true
            },
            {
                key: 'webRagSelectorsStr',
                label: 'Element Selector(s)',
                type: 'text',
                required: false
            },
            {
                key: 'web_rag_max_search_results',
                label: 'Factor',
                type: 'number',
                required: true
            },
        ]
    },

    [AgentSkillType.WEB_SEARCH]: {
        type: AgentSkillType.WEB_SEARCH,
        name: 'Web-Suche',
        description: 'Der Agent kann das Internet durchsuchen und die Ergebnisse nutzen.',
        icon: TbWorldSearch,
        configurationFields: [
            {
                key: 'max_search_results',
                label: 'Anzahl Treffer (max)',
                type: 'number',
                required: true
            },
            {
                key: 'page_description',
                label: 'Optional: Beschreibung der Seite(n)',
                type: 'text',
                required: false
            },
            {
                key: 'urls',
                label: 'Optional: URLs (Komma-getrennt)',
                type: 'text',
                required: false
            },
            {
                key: 'selectors',
                label: 'Optional: Selektoren (Komma-getrennt)',
                type: 'text',
                required: false
            },
        ]
    },

    [AgentSkillType.WEATHER_CURRENT]: {
        type: AgentSkillType.WEATHER_CURRENT,
        name: 'Wetter (aktuell)',
        description: 'Der Agent kennt das aktuelle Wetter.',
        icon: TiWeatherPartlySunny,
        configurationFields: [
            {
                key: 'location',
                label: 'Ort',
                type: 'text',
                required: false
            },
        ]
    },

    [AgentSkillType.WEATHER_FORECAST]: {
        type: AgentSkillType.WEATHER_FORECAST,
        name: 'Wetter (Vorhersage)',
        description: 'Der Agent kennt die Wettervorhersage und kann sie nutzen.',
        icon: TiWeatherSunny,
        configurationFields: [
            {
                key: 'location',
                label: 'Ort',
                type: 'text',
                required: false
            },
        ]
    },

    [AgentSkillType.ROUTING]: {
        type: AgentSkillType.ROUTING,
        name: 'Routen',
        description: 'Der Agent kann Routen berechnen (zu Fuß, Fahrrad, Auto).',
        icon: GiPathDistance,
    },

    [AgentSkillType.POINT_OF_INTERESTS]: {
        type: AgentSkillType.POINT_OF_INTERESTS,
        name: 'Points of Interests',
        description: 'Der Agent kennt interessante Orte (POIs)',
        icon: TfiPin,
        configurationFields: [

            {
                key: 'location',
                label: 'Ort',
                type: 'text',
                required: false
            },
            {
                key: 'radius',
                label: 'Radius (km)',
                type: 'number',
                required: false
            },
        ]
    },

    [AgentSkillType.CALCULATOR]: {
        type: AgentSkillType.CALCULATOR,
        name: 'Taschenrechner',
        description: 'Der Agent kann mathematische Formeln berechnen.',
        icon: PiMathOperationsBold,
    },

    [AgentSkillType.TRANSLATOR]: {
        type: AgentSkillType.TRANSLATOR,
        name: 'Übersetzer',
        description: 'Der Agent kann Text von einer Sprache in eine andere übersetzen.',
        icon: MdTranslate,
    },

    [AgentSkillType.IMAGE_UNDERSTANDING]: {
        type: AgentSkillType.IMAGE_UNDERSTANDING,
        name: 'Bilder verstehen',
        description: 'Der Agent kann Bilder interpertieren.',
        icon: MdImageSearch,
    },

    [AgentSkillType.IMAGE_CREATION]: {
        type: AgentSkillType.IMAGE_CREATION,
        name: 'Bilder erstellen',
        description: 'Der Agent kann Bilder erstellen.',
        icon: LuImagePlus,
    },

    [AgentSkillType.TEXT_TO_SPEECH]: {
        type: AgentSkillType.TEXT_TO_SPEECH,
        name: 'Text zu Audio',
        description: 'Der Agent kann Audio-Dateien erstellen.',
        icon: PiSpeakerHighBold,
    },

    [AgentSkillType.INTERVIEWS]: {
        type: AgentSkillType.INTERVIEWS,
        name: 'Interviews (CIS)',
        description: 'Der Agent kann den Anwender interviewen und die Ergebnisse zum lernen nutzen.',
        icon: TbUserQuestion,
        configurationFields: [
            {
                key: 'instructions',
                label: 'Interview Instructions',
                type: 'textarea',
                required: true
            }
        ]
    },

    [AgentSkillType.DE_VH_KURSE]: {
        type: AgentSkillType.DE_VH_KURSE,
        name: 'VH-Kurse',
        description: 'Der Agent kennt die Kurse der Volkshochschule.',
        icon: RiPresentationLine,
        configurationFields: [
            {
                key: 'max_results',
                label: 'Anzal Treffer (max)',
                type: 'number',
                required: true
            },
            {
                key: 'city',
                label: 'Stadt',
                type: 'text',
                required: false
            },
        ]
    },

    [AgentSkillType.DE_SERVICE_BW]: {
        type: AgentSkillType.DE_SERVICE_BW,
        name: 'Service BW',
        description: 'Der Agent kennt die Diestleistungen aus Service BW.',
        icon: AiOutlineApi,
        configurationFields: [
            {
                key: 'municipality',
                label: 'Municipality',
                type: 'text',
                required: true
            },
            {
                key: 'serviceTypes',
                label: 'Service Types',
                type: 'select',
                required: true,
                options: ['All', 'Citizens', 'Business', 'Administration']
            }
        ]
    },

    [AgentSkillType.DE_DIGITALE_BIB]: {
        type: AgentSkillType.DE_DIGITALE_BIB,
        name: 'Dt. Digitale Bibliothek',
        description: 'Der Agent kann die Inhalte der deutschen digitalen Bibliothek durchsuchen.',
        icon: IoLibraryOutline,
    },

    [AgentSkillType.DE_TEXTARCHIV]: {
        type: AgentSkillType.DE_TEXTARCHIV,
        name: 'Deutsches Textarchiv',
        description: 'Der Agent kann die Inhalte des deutschen Textarchivs durchsuchen.',
        icon: GoArchive,
    },

    [AgentSkillType.OPEN_LIBRARY]: {
        type: AgentSkillType.OPEN_LIBRARY,
        name: 'Open Library',
        description: 'Der Agent kann die Inhalte der Open Library durchsuchen.',
        icon: HiOutlineBuildingLibrary,
    },

    [AgentSkillType.GOOGLE_BOOKS]: {
        type: AgentSkillType.GOOGLE_BOOKS,
        name: 'Google Books',
        description: 'Der Agent kann Google Books durchsuchen.',
        icon: TbBrandGoogle,
    },

    [AgentSkillType.TMDB]: {
        type: AgentSkillType.TMDB,
        name: 'The Movie DB',
        description: 'Der Agent kann The Movie DB durchsuchen.',
        icon: SiThemoviedatabase,
    },

    [AgentSkillType.ARXIV]: {
        type: AgentSkillType.ARXIV,
        name: 'ARXIV',
        description: 'Der Agent kann Paper und Publikationen aus arXiv durchsuchen.',
        icon: SiArxiv,
    },

    [AgentSkillType.ENAIO]: {
        type: AgentSkillType.ENAIO,
        name: 'ENAIO',
        description: 'Der Agent kann Dokumente aus dem DMS enaio durchsuchen.',
        icon: TbDatabaseLeak,
    },

    [AgentSkillType.SHAREPOINT]: {
        type: AgentSkillType.SHAREPOINT,
        name: 'MS Sharepoint',
        description: 'Der Agent kann Dokumente aus MS Sharepoint durchsuchen.',
        icon: TiVendorMicrosoft,
    },

    [AgentSkillType.PIROBASE]: {
        type: AgentSkillType.PIROBASE,
        name: 'Pirobase',
        description: 'Der Agent kann Dokumente aus dem DMS Pirobase durchsuchen.',
        icon: TbDatabaseSearch,
    },

    [AgentSkillType.STAFFBASE]: {
        type: AgentSkillType.STAFFBASE,
        name: 'Staffbase',
        description: 'Der Agent kann Dokumente aus dem DMS Staffbase durchsuchen.',
        icon: TbDatabaseSearch,
    },

    [AgentSkillType.OPARL]: {
        type: AgentSkillType.OPARL,
        name: 'oParl API',
        description: 'Der Agent kann Dokumente aus einer oParl API nutzen.',
        icon: RiGovernmentLine,
        configurationFields: [
            {
                key: 'entry_url',
                label: 'Url',
                type: 'text',
                required: true
            },
        ]
    },

    [AgentSkillType.CUSTOM_API]: {
        type: AgentSkillType.CUSTOM_API,
        name: 'Eigene API',
        description: 'Der Agent kann Dokumente aus einer API nutzen.',
        icon: FaCode,
        configurationFields: [
            {
                key: 'endpoint',
                label: 'API Endpoint',
                type: 'url',
                required: true
            },
            {
                key: 'method',
                label: 'HTTP Method',
                type: 'select',
                required: true,
                options: ['GET', 'POST']
            },
            {
                key: 'headers',
                label: 'Headers (JSON)',
                type: 'text',
                required: false
            },
            {
                key: 'authentication',
                label: 'Authentication Type',
                type: 'select',
                required: true,
                options: ['None', 'API Key', 'Bearer Token', 'Basic Auth']
            },
            {
                key: 'authValue',
                label: 'Authentication Value',
                type: 'text',
                required: false
            }
        ]
    },

    [AgentSkillType.KOMMONE_EVENT]: {
        type: AgentSkillType.KOMMONE_EVENT,
        name: 'komm.One Event API',
        description: 'Der Agent kann Veranstaltungen der Komm.ONE API abrufen.',
        icon: MdOutlineEventNote,
        configurationFields: [
            {
                key: 'apiKey',
                label: 'API Key',
                type: 'text',
                required: true
            },
            {
                key: 'eventTypes',
                label: 'Event Types',
                type: 'select',
                required: true,
                options: ['All', 'Public', 'Internal', 'Administrative']
            },
            {
                key: 'municipality',
                label: 'Municipality',
                type: 'text',
                required: true
            }
        ]
    },

}; 